import React, { createElement, useRef, useLayoutEffect } from 'react';
import PropTypes from 'prop-types';
import ResizeObserver from 'resize-observer-polyfill';

// type RenderTruncator = ({
//   hiddenItemsCount,
// }: {
//   hiddenItemsCount: number;
// }) => React.ReactNode;

// interface Props {
//   renderTruncator: RenderTruncator;
//   children?: React.ReactNode;
//   alwaysShowTruncator?: boolean;
//   className?: string;
//   itemClassName?: string;
//   truncatorClassName?: string;
//   style?: React.CSSProperties;
// }

const TruncatedList = ({
  renderTruncator,
  alwaysShowTruncator,
  children,
  className,
  style,
  ListComponent,
  ListComponentProps,
  ListItemComponent,
  ListItemComponentProps,
}) => {
  const containerRef = useRef(null);

  useLayoutEffect(() => {
    const truncate = () => {
      if (!containerRef.current) return;

      containerRef.current.style.overflow = 'hidden';

      const childNodes = Array.from(containerRef.current.children);

      for (const node of childNodes) {
        node.hidden = true;
      }

      if (childNodes.length === 0) {
        return;
      }

      let i; // Declare outside the loop so we can check it afterward
      for (i = 0; i < childNodes.length; i += 2) {
        const itemEl = childNodes[i];
        const truncatorEl = childNodes[i + 1];

        itemEl.hidden = false;
        truncatorEl.hidden = false;
        const truncatorRect = truncatorEl.getBoundingClientRect();
        const containerRect = containerRef.current.getBoundingClientRect();
        truncatorEl.hidden = true;

        // If truncator is outside of the container
        if (
          truncatorRect.bottom > containerRect.bottom ||
          truncatorRect.right > containerRect.right
        ) {
          itemEl.hidden = true;
          if (i > 0) {
            childNodes[i - 1].hidden = false;
          }
          break;
        }
      }

      // If all items were inside the container but we still wish to show the truncator
      if (i === childNodes.length && alwaysShowTruncator) {
        childNodes[childNodes.length - 1].hidden = false;
      }
    };

    truncate();

    const resizeObserver = new ResizeObserver(entries => {
      entries.forEach(truncate);
    });

    // Copy to a variable so the ref in the cleanup effect targets the correct node
    const containerEl = containerRef.current;

    if (containerEl) {
      resizeObserver.observe(containerEl);
    }

    return () => {
      if (containerEl) {
        resizeObserver.unobserve(containerEl);
      }
    };
  }, [children, alwaysShowTruncator, className, style]);

  const childArray = React.Children.toArray(children);

  const getlistItem = item =>
    createElement(
      ListItemComponent,
      { ...(item.props || {}), ...ListItemComponentProps },
      item.children,
    );

  const items = childArray.map((item, i) => (
    <React.Fragment key={`${item}${i}`}>
      {getlistItem({ children: item })}
      {getlistItem({
        children: renderTruncator({ hiddenItemsCount: childArray.length - 1 - i }),
        props: { hidden: true },
      })}
    </React.Fragment>
  ));

  return createElement(
    ListComponent,
    {
      ref: containerRef,
      style,
      className,
      ...ListComponentProps,
    },
    items,
  );
};

TruncatedList.defaultProps = {
  ListComponent: 'div',
  ListComponentProps: {},
  ListItemComponent: 'div',
  ListItemComponentProps: {},
};

TruncatedList.propTypes = {
  renderTruncator: PropTypes.func,
  alwaysShowTruncator: PropTypes.bool,
  children: PropTypes.node,
  ListComponent: PropTypes.elementType,
  ListComponentProps: PropTypes.object,
  ListItemComponent: PropTypes.elementType,
  ListItemComponentProps: PropTypes.object,
  className: PropTypes.string,
  itemClassName: PropTypes.string,
  truncatorClassName: PropTypes.string,
  style: PropTypes.object,
};

export default TruncatedList;
